import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Logo, Check, ArrowRight } from '../components/Icons.jsx';
import { Intro, Done, mainStyle } from './VennArchitect.jsx';
import DrillResultBar from '../components/DrillResultBar.jsx';

const shapes = ['▲', '■', '●', '◆'];

function makePuzzle(level) {
  const numUnknowns = level === 'easy' ? 2 : level === 'medium' ? 2 : 3;
  const valueRange = level === 'easy' ? 9 : level === 'medium' ? 12 : 15;
  const vals = [];
  for (let i = 0; i < numUnknowns; i++) vals.push(1 + Math.floor(Math.random() * valueRange));

  // build 2 linear equations
  const c1 = Array.from({ length: numUnknowns }, () => 1 + Math.floor(Math.random() * 3));
  const c2 = Array.from({ length: numUnknowns }, () => 1 + Math.floor(Math.random() * 3));
  const r1 = c1.reduce((sum, c, i) => sum + c * vals[i], 0);
  const r2 = c2.reduce((sum, c, i) => sum + c * vals[i], 0);

  const targetCoeffs = Array.from({ length: numUnknowns }, () => 1 + Math.floor(Math.random() * 2));
  const answer = targetCoeffs.reduce((sum, c, i) => sum + c * vals[i], 0);

  const renderEq = (coeffs, result) => {
    const parts = coeffs.map((c, i) => {
      const sh = shapes[i];
      if (c === 0) return '';
      if (c === 1) return sh;
      return `${c}×${sh}`;
    }).filter(Boolean);
    return `${parts.join(' + ')} = ${result}`;
  };
  const target = targetCoeffs.map((c, i) => c === 0 ? '' : c === 1 ? shapes[i] : `${c}×${shapes[i]}`).filter(Boolean).join(' + ');

  return {
    eq1: renderEq(c1, r1),
    eq2: renderEq(c2, r2),
    target, answer, vals
  };
}

export default function ShapeEquations() {
  const [stage, setStage] = useState('intro');
  const [level, setLevel] = useState('medium');
  const [ucatsen, setUcatsen] = useState(false);
  const [q, setQ] = useState(null);
  const [input, setInput] = useState('');
  const [score, setScore] = useState({ correct: 0, wrong: 0, attempts: 0 });
  const [secondsLeft, setSecondsLeft] = useState(120);
  const [flash, setFlash] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const duration = ucatsen ? 150 : 120;

  useEffect(() => {
    if (stage !== 'drill') return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => { if (s <= 1) { clearInterval(timerRef.current); setStage('done'); return 0; } return s - 1; });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [stage]);

  const start = () => {
    setScore({ correct: 0, wrong: 0, attempts: 0 });
    setSecondsLeft(duration); setInput(''); setFlash(null);
    setQ(makePuzzle(level)); setStage('drill');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const submit = () => {
    if (!q || input.trim() === '') return;
    const guess = Number(input);
    const ok = guess === q.answer;
    setScore((s) => ({ correct: s.correct + (ok ? 1 : 0), wrong: s.wrong + (ok ? 0 : 1), attempts: s.attempts + 1 }));
    setFlash({ ok, msg: ok ? '✓' : `${q.answer}` });
    setInput(''); setQ(makePuzzle(level));
    setTimeout(() => setFlash(null), 700);
    inputRef.current?.focus();
  };

  const accuracy = score.attempts > 0 ? Math.round((score.correct / score.attempts) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={drillHeader}>
        <Link to="/practice" style={drillExit}><ArrowLeft width="16" height="16" />Exit</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight />
          <strong style={titleStyle}>Shape equations</strong>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: secondsLeft < 20 ? '#b91c1c' : 'var(--text-2)', minWidth: 60, textAlign: 'right' }}>
          {stage === 'drill' && `${secondsLeft}s`}
        </div>
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="DM" title="Shape equations"
          body="Simultaneous equations disguised as shapes. Real UCAT gives you ~8 seconds per puzzle — this drill builds that reflex."
          how={['Pick difficulty', 'Solve the equation involving the target shapes', 'Enter to submit — quickfire']}
          difficulty={level} setDifficulty={setLevel}
          difficultyOptions={[
            { v: 'easy', label: 'Foundation', sub: '2 shapes · single-digit values' },
            { v: 'medium', label: 'Standard', sub: '2 shapes · larger numbers' },
            { v: 'hard', label: 'UCAT-style', sub: '3 shapes · mixed coefficients' }
          ]}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
        />
      )}

      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>
            <span style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{level}</span>
            <span><strong style={{ color: 'var(--live-green)' }}>{score.correct}</strong> · <strong style={{ color: '#b91c1c' }}>{score.wrong}</strong></span>
          </div>

          <div style={{
            background: flash?.ok === true ? '#e7f7ef' : flash?.ok === false ? '#fee2e2' : '#fff',
            border: `2px solid ${flash?.ok === true ? 'var(--live-green)' : flash?.ok === false ? '#b91c1c' : 'var(--border)'}`,
            borderRadius: 16, padding: 'clamp(20px, 4vw, 32px)', marginBottom: 16,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18
          }}>
            <div style={eqStyle}>{q.eq1}</div>
            <div style={eqStyle}>{q.eq2}</div>
            <div style={{ borderTop: '1px solid var(--border)', width: '100%', margin: '6px 0' }} />
            <div style={{ ...eqStyle, color: 'var(--brand-purple)' }}>{q.target} = ?</div>
            {flash && (
              <div style={{ fontSize: 22, fontWeight: 800, color: flash.ok ? 'var(--live-green)' : '#b91c1c' }}>{flash.msg}</div>
            )}
          </div>

          <input
            ref={inputRef} type="number" inputMode="numeric"
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder="Your answer"
            style={inputStyle}
          />
          <div style={{ marginTop: 14, display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={submit} className="btn btn-primary">Submit <Check width="14" height="14" /></button>
            <button onClick={() => setQ(makePuzzle(level))} className="btn btn-secondary">Skip <ArrowRight width="14" height="14" /></button>
          </div>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="DM" title="Shape equations" stats={[
          { label: 'Correct', value: score.correct },
          { label: 'Wrong', value: score.wrong },
          { label: 'Accuracy', value: `${accuracy}%` }
        ]} onRestart={start}
          extra={<DrillResultBar slug="shape-equations" score={score.correct} accuracyPct={accuracy} metadata={{ level, ucatsen }} />}
        />
      )}
    </div>
  );
}

const drillHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10 };
const drillExit = { display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 };
const titleStyle = { fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' };
const eqStyle = { fontSize: 'clamp(22px, 4vw, 28px)', fontWeight: 700, fontFamily: 'Montserrat, sans-serif', color: 'var(--brand-deep)' };
const inputStyle = { width: '100%', padding: '16px 18px', fontSize: 22, fontWeight: 700, textAlign: 'center', border: '2px solid var(--border-strong)', borderRadius: 14, fontFamily: 'Montserrat, sans-serif' };
